import type {
  DriverAnalyticsSummary,
  DriverDeliveryCardItem,
  DriverDeliveryEnrichedRow,
  DriverDeliveryRow,
  DriverDeliveriesSummary,
} from "../types/driverDeliveries";

export const BONUS_BEFORE_21 = 500;
export const BONUS_21_TO_22 = 200;

export const VEHICLE_COMMISSION: Record<string, number> = {
  moto: 0.3,
  auto: 0.25,
  utilitario: 0.2,
  furgon: 0.1,
};

export const toISODate = (value?: string | null): string => {
  if (!value) return "";
  const s = String(value).trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

  const match = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (match) {
    const dd = match[1].padStart(2, "0");
    const mm = match[2].padStart(2, "0");
    const yyyy = match[3];
    return `${yyyy}-${mm}-${dd}`;
  }

  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  return "";
};

export const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;

  let d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;

  if (/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}/.test(s)) {
    d = new Date(s.replace(" ", "T"));
    if (!Number.isNaN(d.getTime())) return d;
  }

  return null;
};

export const withinRange = (iso: string, from: string, to: string): boolean => {
  if (!iso) return false;
  if (from && iso < from) return false;
  if (to && iso > to) return false;
  return true;
};

export const isDelivered = (estado?: string): boolean => {
  const s = String(estado || "").toLowerCase();
  return s.includes("entreg");
};

export const formatMoney = (value: number): string => {
  return value.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const nowISODate = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const getLast7DaysRange = (): { from: string; to: string } => {
  const to = nowISODate();
  const d = new Date();
  d.setDate(d.getDate() - 6);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return {
    from: `${yyyy}-${mm}-${dd}`,
    to,
  };
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;

  if (typeof value !== "string") return 0;

  const raw = value.trim();
  if (!raw) return 0;

  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  let normalized = raw;

  if (hasComma && hasDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalized = raw.replace(",", ".");
  } else if (hasDot) {
    const decimalDotPattern = /^\d+\.\d+$/;
    normalized = decimalDotPattern.test(raw) ? raw : raw.replace(/\./g, "");
  }

  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeCommissionPercentage = (value: unknown): number => {
  const numeric = toNumber(value);

  if (!numeric) return 0;

  if (numeric > 0 && numeric <= 1) return numeric;

  if (numeric > 1) return numeric / 100;

  return 0;
};

export const enrichDriverDeliveries = (
  rows: DriverDeliveryRow[],
  commission: number
): DriverDeliveryEnrichedRow[] => {
  const base = rows.map((r) => {
    const bruto = toNumber(r.precio_chofer);

    const porcentajeComisionLegacy = normalizeCommissionPercentage(
      r.porcentaje_chofer
    );

    const porcentajeComision =
      porcentajeComisionLegacy > 0 ? porcentajeComisionLegacy : commission || 0;

    const comision = bruto * porcentajeComision;
    const neto = bruto - comision;

    const colectaDay = toISODate(String(r.fecha_colecta || ""));

    return {
      ...r,
      _bruto_envio: bruto,
      _porcentaje_comision: porcentajeComision,
      _comision_envio: comision,
      _neto_envio: neto,
      _bonus_candidate: 0,
      _colecta_day: colectaDay,
      _late_after_23: false,
      BONUS: 0,
      _penalized_day: false,
    };
  });

  return base;
};

export const buildDriverDeliveriesSummary = (
  rows: DriverDeliveryEnrichedRow[]
): DriverDeliveriesSummary => {
  const totalEnvios = rows.length;

  const brutoTotal = rows.reduce(
    (acc, row) => acc + Number(row._bruto_envio || 0),
    0
  );

  const comisionTotal = rows.reduce(
    (acc, row) => acc + Number(row._comision_envio || 0),
    0
  );

  const netoTotal = rows.reduce(
    (acc, row) => acc + Number(row._neto_envio || 0),
    0
  );

  return {
    totalEnvios,
    brutoTotal,
    comisionTotal,
    netoTotal,
    bonusTotal: 0,
    totalACobrar: netoTotal,
    penalizedDays: [],
  };
};

export const buildDriverDeliveryCards = (
  rows: DriverDeliveryEnrichedRow[]
): DriverDeliveryCardItem[] => {
  return rows.map((row, index) => ({
    key: row.numero_tracking ? `${row.numero_tracking}-${index}` : `row-${index}`,
    row,
    direccion: String(row.direccion || "-"),
    estado: String(row.estado || "-"),
    nombre: String(row.nombre_fantasia || "-"),
    bruto: Number(row._bruto_envio || 0),
    porcentajeComision: Number(row._porcentaje_comision || 0),
    comision: Number(row._comision_envio || 0),
    neto: Number(row._neto_envio || 0),
    bonus: 0,
  }));
};

export const buildDriverAnalyticsSummary = (
  rows: DriverDeliveryEnrichedRow[]
): DriverAnalyticsSummary => {
  let delivered = 0;
  let pending = 0;
  let penalized = 0;

  for (const row of rows) {
    if (row._penalized_day) {
      penalized += 1;
    } else if (isDelivered(String(row.estado || ""))) {
      delivered += 1;
    } else {
      pending += 1;
    }
  }

  return {
    status: {
      delivered,
      pending,
      penalized,
      total: rows.length,
    },
    bonusByDay: [],
  };
};

type LegacyUserLike = {
  username?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  legacyDriverName?: string | null;
};

export const buildLegacyDriverName = (user?: LegacyUserLike | null): string => {
  const legacyDriverName = user?.legacyDriverName?.trim();

  if (legacyDriverName) {
    return legacyDriverName;
  }

  const firstName = user?.first_name || user?.firstName || "";
  const lastName = user?.last_name || user?.lastName || "";

  const fullName = [firstName, lastName]
    .map((value) => String(value).trim())
    .filter(Boolean)
    .join(" ")
    .trim();

  if (fullName) {
    return fullName;
  }

  return user?.name?.trim() || user?.username?.trim() || "";
};

export const normalizeLegacyDriverName = (value: string): string => {
  return value.toLowerCase().replace(/\s+/g, " ").trim();
};

export const buildLegacyDriverNameCandidates = (value: string): string[] => {
  const clean = value.replace(/\s+/g, " ").trim();

  if (!clean) return [];

  return Array.from(
    new Set([
      clean,
      clean.toLowerCase(),
      clean.toUpperCase(),
    ])
  );
};

export const resolveLegacyDriverName = (
  user?: LegacyUserLike | null
): string => {
  return normalizeLegacyDriverName(buildLegacyDriverName(user));
};